import * as Popover from '@radix-ui/react-popover';
import { format } from 'date-fns';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import type { ClassNames } from 'react-day-picker';
import { DayPicker } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { Button } from '~/chords/Button';
import { Icon } from '~/chords/Icon';
import { cn } from '~/chords/lib/cn';
import { type VariantProps } from '~/chords/lib/variants';

import { ChevronIcon } from '../ChevronIcon/ChevronIcon';
import { inputVariants } from '../Input/variants';
import { dayPickerClassNames } from './DatePicker.styles';
import type { DatePickerAppearance, DatePickerVariants } from './utils';
import {
  datePickerVariants,
  formatDate,
  formatTime,
  FUTURE_LIMIT,
  getDisabledDates,
  TODAY,
  valueIncludesTime,
} from './utils';

export interface DatePickerProps
  extends Pick<
    VariantProps<typeof datePickerVariants>,
    'variant' | 'appearance'
  > {
  value?: Date;
  onApply: (date: Date) => void;
  /**
   * When provided, a clear button is revealed on hover or focus of the trigger
   * while a date is selected. Clicking it closes the calendar and calls
   * `onClear`; the parent must reset `value`.
   */
  onClear?: () => void;
  minDate?: Date;
  allowFutureDates?: boolean;
  placeholder?: string;
  highlightToday?: boolean;
  variant?: DatePickerVariants;
  appearance?: DatePickerAppearance;
  timeOptions?: {
    isEnabled: boolean;
    isRequired?: boolean;
    showInitial?: boolean;
    label: string;
  };
}

export function DatePicker({
  onApply,
  onClear,
  value,
  minDate,
  allowFutureDates = true,
  placeholder = 'Select Date',
  highlightToday = false,
  variant = 'default',
  appearance = 'dropdown',
  timeOptions,
}: DatePickerProps) {
  const timeInputId = useId();

  const { t } = useTranslation('chords');
  const [open, setOpen] = useState(false);
  const [datetime, setDatetime] = useState<Date | undefined>(value);
  const isTimeRequired = timeOptions?.isRequired ?? true;
  const showInitialTime = timeOptions?.showInitial ?? true;
  const [optionalTimeIncluded, setOptionalTimeIncluded] = useState(() =>
    Boolean(
      value && !isTimeRequired && (valueIncludesTime(value) || showInitialTime),
    ),
  );
  const optionalTimeIsIncluded = useCallback(
    (date: Date | undefined) =>
      Boolean(
        date &&
          (isTimeRequired || valueIncludesTime(date) || optionalTimeIncluded),
      ),
    [isTimeRequired, optionalTimeIncluded],
  );
  const [timeSelected, setTimeSelected] = useState(() => {
    if (!value || !timeOptions?.isEnabled) {
      return false;
    }

    if (showInitialTime) {
      return true;
    }

    return Boolean(value && (isTimeRequired || valueIncludesTime(value)));
  });

  useEffect(() => {
    if (!value) {
      setOptionalTimeIncluded(false);
    } else if (!isTimeRequired && valueIncludesTime(value)) {
      setOptionalTimeIncluded(true);
    }
  }, [isTimeRequired, value]);

  const syncDraftFromValue = useCallback(
    (nextValue: Date | undefined) => {
      setDatetime(nextValue);

      if (!timeOptions?.isEnabled) {
        return;
      }

      setTimeSelected(Boolean(nextValue && optionalTimeIsIncluded(nextValue)));
    },
    [optionalTimeIsIncluded, timeOptions?.isEnabled],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      syncDraftFromValue(value);
    },
    [syncDraftFromValue, value],
  );

  const handleSelectDateAndTime = useCallback(
    (date: Date) => {
      const nextDatetime = new Date(date);

      if (datetime && (isTimeRequired || timeSelected)) {
        nextDatetime.setHours(
          datetime.getHours(),
          datetime.getMinutes(),
          datetime.getSeconds(),
          datetime.getMilliseconds(),
        );
      } else {
        nextDatetime.setHours(0, 0, 0, 0);
      }

      setDatetime(nextDatetime);

      if (isTimeRequired) {
        setTimeSelected(true);
      }
    },
    [datetime, isTimeRequired, timeSelected],
  );

  const handleSelectDate = useCallback(
    (date: Date) => {
      if (timeOptions?.isEnabled) {
        handleSelectDateAndTime(date);

        return;
      }

      onApply(date);
      handleOpenChange(false);
    },
    [
      onApply,
      timeOptions?.isEnabled,
      handleSelectDateAndTime,
      handleOpenChange,
    ],
  );

  const handleSelectTime = useCallback(
    (time: string) => {
      if (!datetime || !time) {
        return;
      }

      const [hours, minutes] = time.split(':').map(Number);

      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
        return;
      }

      const nextDatetime = new Date(datetime);

      nextDatetime.setHours(
        hours,
        minutes,
        datetime.getSeconds(),
        datetime.getMilliseconds(),
      );

      setDatetime(nextDatetime);
      setTimeSelected(true);
    },
    [datetime],
  );

  const isPlaceholder = !value;

  const handleCancel = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  const handleConfirm = useCallback(() => {
    if (!datetime) {
      return;
    }

    if (!isTimeRequired) {
      setOptionalTimeIncluded(timeSelected);
    }

    onApply(datetime);
    handleOpenChange(false);
  }, [datetime, handleOpenChange, isTimeRequired, onApply, timeSelected]);

  const disabledDates = useMemo(
    () => getDisabledDates(minDate, allowFutureDates),
    [minDate, allowFutureDates],
  );

  const classNames: Partial<ClassNames> = { ...dayPickerClassNames };

  if (highlightToday) {
    classNames.today = '[&>button]:underline [&>button]:underline-offset-4';
  }

  const chevronDirection = open ? 'up' : 'down';
  const {
    field,
    trigger,
    text,
    clearButton,
    trailingIcon,
    timePickerGroup,
    timePickerClearButton,
  } = datePickerVariants({
    variant,
    appearance,
    isPlaceholder,
  });

  const showTimeInField = useMemo(() => {
    if (!timeOptions?.isEnabled || !value) {
      return false;
    }

    return optionalTimeIsIncluded(value);
  }, [optionalTimeIsIncluded, timeOptions?.isEnabled, value]);

  const timeInputValue = useMemo(() => {
    if (!datetime) {
      return '';
    }

    if (!isTimeRequired && !timeSelected) {
      return '';
    }

    return format(datetime, 'HH:mm');
  }, [datetime, isTimeRequired, timeSelected]);

  const displayValue = useMemo(() => {
    if (timeOptions?.isEnabled) {
      return value ? (
        <>
          {formatDate(value)}
          {showTimeInField ? (
            <>
              <span className="text-subtle">{' • '}</span>
              {formatTime(value)}
            </>
          ) : null}
        </>
      ) : (
        placeholder
      );
    }

    return formatDate(value) ?? placeholder;
  }, [value, placeholder, showTimeInField, timeOptions?.isEnabled]);

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Anchor asChild>
        <div data-testid="date-picker-field" className={field()}>
          <Popover.Trigger asChild>
            <button
              type="button"
              data-testid="date-picker-trigger"
              data-date-picker-trigger=""
              aria-invalid={variant === 'error'}
              className={trigger()}
            >
              <span className={text()}>{displayValue}</span>
            </button>
          </Popover.Trigger>

          {onClear && value ? (
            <button
              type="button"
              data-testid="date-picker-clear"
              aria-label={t('datePicker.clearDate')}
              onClick={() => {
                onClear();
                setOpen(false);
              }}
              className={clearButton()}
            >
              <Icon type="xmark" size="sm" aria-hidden />
            </button>
          ) : null}

          <span aria-hidden className={trailingIcon()}>
            {appearance === 'dropdown' ? (
              <ChevronIcon direction={chevronDirection} />
            ) : null}

            {appearance === 'input' ? (
              <Icon type="calendar" variant="light" size="sm" />
            ) : null}
          </span>
        </div>
      </Popover.Anchor>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          className="z-10 mt-xs w-[18rem] overflow-hidden rounded-sm border bg-level-03 border-emphasis-subtle"
        >
          <div className="flex flex-col">
            <DayPicker
              showOutsideDays
              required={true}
              mode="single"
              captionLayout="dropdown"
              disabled={disabledDates}
              startMonth={minDate || TODAY}
              endMonth={allowFutureDates ? FUTURE_LIMIT : TODAY}
              selected={timeOptions?.isEnabled ? datetime : value}
              onSelect={handleSelectDate}
              numberOfMonths={1}
              classNames={classNames}
              formatters={{
                formatWeekdayName: (weekday) => format(weekday, 'ccccc'),
                formatMonthDropdown: (month) => format(month, 'MMMM'),
              }}
              data-testid="date-picker-calendar"
            />
            {timeOptions?.isEnabled ? (
              <div className="border-t-px p-lg bg-level-02 border-emphasis-subtle">
                <label
                  htmlFor={timeInputId}
                  className="mb-md block text-default"
                >
                  {timeOptions?.label}
                </label>
                <div className={timePickerGroup()}>
                  <input
                    id={timeInputId}
                    data-testid="date-time-picker-time-input"
                    aria-invalid={variant === 'error'}
                    disabled={!datetime}
                    type="time"
                    value={timeInputValue}
                    onChange={(e) => handleSelectTime(e.target.value)}
                    className={cn(
                      inputVariants({
                        state: variant,
                        rounded: 'xsmall',
                        size: 'large',
                      }),
                      'pr-xl',
                      '[&::-webkit-calendar-picker-indicator]:absolute',
                      '[&::-webkit-calendar-picker-indicator]:inset-y-0',
                      '[&::-webkit-calendar-picker-indicator]:right-0',
                      '[&::-webkit-calendar-picker-indicator]:h-full',
                      '[&::-webkit-calendar-picker-indicator]:w-xl',
                      '[&::-webkit-calendar-picker-indicator]:cursor-pointer',
                      '[&::-webkit-calendar-picker-indicator]:[opacity:0]',
                    )}
                    aria-describedby={
                      !datetime ? `${timeInputId}-description` : undefined
                    }
                  />
                  {!datetime ? (
                    <span id={`${timeInputId}-description`} className="sr-only">
                      {t('datePicker.disabledDescription')}
                    </span>
                  ) : null}
                  {timeOptions?.isEnabled && !isTimeRequired && timeSelected ? (
                    <button
                      type="button"
                      data-testid="time-picker-clear"
                      aria-label={t('datePicker.clearTime')}
                      onClick={() => {
                        if (!datetime) {
                          return;
                        }

                        // clear the time by setting the date to the same date but with the time set to 00:00
                        const newDate = new Date(datetime);

                        newDate.setHours(0, 0, 0, 0);
                        handleSelectDateAndTime(newDate);
                        setTimeSelected(false);
                        setDatetime(newDate);
                      }}
                      className={cn(clearButton(), timePickerClearButton())}
                    >
                      <Icon type="xmark" size="sm" aria-hidden />
                    </button>
                  ) : null}
                  <span
                    className={cn(
                      'pointer-events-none absolute right-md top-1/2 -translate-y-1/2 text-default',
                      { 'text-disabled': !datetime },
                    )}
                  >
                    <Icon
                      type="clock"
                      variant="light"
                      size="sm"
                      aria-hidden="true"
                    />
                  </span>
                </div>
                <div className="mt-lg flex justify-end gap-md">
                  <Button
                    type="button"
                    onClick={handleCancel}
                    variant="tertiary"
                  >
                    {t('datePicker.cancel')}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleConfirm}
                    disabled={!datetime || (isTimeRequired && !timeSelected)}
                  >
                    {t('datePicker.confirm')}
                  </Button>
                </div>
              </div>
            ) : undefined}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

DatePicker.displayName = 'DatePicker';
